/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import {
    DataGridPremium,
    GridColDef,
    useGridApiRef,
    GridToolbar,
} from "@mui/x-data-grid-premium";
import dayjs from "dayjs";
import FastAPIClient from "../../client";
import config from "../../config";
import { frFR } from "@mui/x-data-grid/locales";
import {
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    OutlinedInput,
    TextField,
    SelectChangeEvent,
} from "@mui/material";

const client = new FastAPIClient(config);

type ClientTransactionSummary = {
    year: number;
    month: string;
    client_name: string;
    brand: string;
    product_name: string;
    product_level_1: string;
    total_volume: number;
    total_revenue: number;
    warehouse_name: string;
    city: string;
};

// Helper type to represent a client's aggregated purchasing data
type ClientPurchases = {
    transactions: ClientTransactionSummary[];
    brands: Set<string>;
    products: Set<string>;
    product_level_ones: Set<string>;
};

const ClientBrandProductTable = (): React.ReactElement => {
    const [clientData, setClientData] = useState<Map<
        string,
        ClientPurchases
    > | null>(null); // Store data grouped by client
    const [filteredData, setFilteredData] = useState<
        ClientTransactionSummary[] | null
    >(null); // Store filtered data for the table
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
    const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
    const [selectedExcludedBrands, setSelectedExcludedBrands] = useState<
        string[]
    >([]);
    const [selectedExcludedProducts, setSelectedExcludedProducts] = useState<
        string[]
    >([]);
    const [brandLogic, setBrandLogic] = useState<"and" | "or">("or"); // 'and' or 'or'
    const [productLogic, setProductLogic] = useState<"and" | "or">("or"); // 'and' or 'or'
    const [productLevelOneLogic, setProductLevelOneLogic] = useState<
        "and" | "or"
    >("or"); // 'and' or 'or'
    const [availableBrands, setAvailableBrands] = useState<string[]>([]);
    const [availableProducts, setAvailableProducts] = useState<string[]>([]);
    const [brandSearchTerm, setBrandSearchTerm] = useState("");
    const [productSearchTerm, setProductSearchTerm] = useState("");
    const [excludedBrandSearchTerm, setExcludedBrandSearchTerm] = useState("");
    const [excludedProductSearchTerm, setExcludedProductSearchTerm] =
        useState("");
    const [selectedProductLevelOnes, setSelectedProductLevelOnes] = useState<
        string[]
    >([]);
    const [
        selectedExcludedProductLevelOnes,
        setSelectedExcludedProductLevelOnes,
    ] = useState<string[]>([]);
    const [productLevelOneSearchTerm, setProductLevelOneSearchTerm] =
        useState("");
    const [
        excludedProductLevelOneSearchTerm,
        setExcludedProductLevelOneSearchTerm,
    ] = useState("");
    const [availableProductLevelOnes, setAvailableProductLevelOnes] = useState<
        string[]
    >([]);

    const apiRef = useGridApiRef();

    const fetchAndProcessData = async () => {
        setLoading(true);
        try {
            const response = await client.fetchClientBrandProductStats();
            console.log(response);
            const fetchedData: ClientTransactionSummary[] =
                response.results || [];

            // Group data by client and extract unique brands and products per client
            const groupedClientData = new Map<string, ClientPurchases>();
            const allBrands = new Set<string>();
            const allProducts = new Set<string>();
            const allProductLevelOnes = new Set<string>();

            fetchedData.forEach((item) => {
                if (!groupedClientData.has(item.client_name)) {
                    groupedClientData.set(item.client_name, {
                        transactions: [],
                        brands: new Set(),
                        products: new Set(),
                        product_level_ones: new Set(),
                    });
                }
                const clientEntry = groupedClientData.get(item.client_name)!;
                clientEntry.transactions.push(item);
                if (item.brand) clientEntry.brands.add(item.brand);
                if (item.product_name)
                    clientEntry.products.add(item.product_name);
                if (item.product_level_1)
                    clientEntry.product_level_ones.add(item.product_level_1);

                if (item.brand) allBrands.add(item.brand);
                if (item.product_name) allProducts.add(item.product_name);
                if (item.product_level_1)
                    allProductLevelOnes.add(item.product_level_1);
            });

            setClientData(groupedClientData);
            setAvailableBrands(
                Array.from(allBrands).filter(
                    (brand) => brand !== undefined && brand !== null
                ) as string[]
            );
            setAvailableProducts(
                Array.from(allProducts).filter(
                    (product) => product !== undefined && product !== null
                ) as string[]
            );
            setAvailableProductLevelOnes(
                Array.from(allProductLevelOnes).filter(
                    (level) => level !== undefined && level !== null
                ) as string[]
            );

            // Apply initial filtering (none initially)
            applyFilters(
                groupedClientData,
                selectedBrands,
                selectedProducts,
                selectedExcludedBrands,
                selectedExcludedProducts,
                brandLogic,
                productLogic,
                productLevelOneLogic,
                selectedProductLevelOnes,
                selectedExcludedProductLevelOnes
            );
        } catch (err) {
            console.error("Failed to fetch client transaction summary", err);
            setClientData(null);
            setFilteredData(null);
            setAvailableBrands([]);
            setAvailableProducts([]);
            setAvailableProductLevelOnes([]);
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = (
        groupedDataToFilter: Map<string, ClientPurchases> | null,
        brands: string[],
        products: string[],
        excludedBrands: string[],
        excludedProducts: string[],
        currentBrandLogic: "and" | "or",
        currentProductLogic: "and" | "or",
        currentProductLevelOneLogic: "and" | "or",
        productLevelOnes: string[],
        excludedProductLevelOnes: string[]
    ) => {
        if (!groupedDataToFilter) {
            setFilteredData(null);
            return;
        }

        const filteredClientsData: ClientTransactionSummary[] = [];

        // Process the map entries in a safe, non-recursive manner
        const entries = Array.from(groupedDataToFilter.entries());
        for (let i = 0; i < entries.length; i++) {
            const [, clientPurchaseData] = entries[i];
            const clientBrands = clientPurchaseData.brands;
            const clientProducts = clientPurchaseData.products;
            const clientTransactions = clientPurchaseData.transactions;
            const clientProductLevelOnes =
                clientPurchaseData.product_level_ones;

            // Apply inclusion filters
            let passesBrandInclusion = true;
            if (brands.length > 0) {
                if (currentBrandLogic === "or") {
                    passesBrandInclusion = false;
                    for (const brand of brands) {
                        if (clientBrands.has(brand)) {
                            passesBrandInclusion = true;
                            break;
                        }
                    }
                } else {
                    // 'and' logic
                    passesBrandInclusion = true;
                    for (const brand of brands) {
                        if (!clientBrands.has(brand)) {
                            passesBrandInclusion = false;
                            break;
                        }
                    }
                }
            }

            let passesProductInclusion = true;
            if (products.length > 0) {
                if (currentProductLogic === "or") {
                    passesProductInclusion = false;
                    for (const product of products) {
                        if (clientProducts.has(product)) {
                            passesProductInclusion = true;
                            break;
                        }
                    }
                } else {
                    // 'and' logic
                    passesProductInclusion = true;
                    for (const product of products) {
                        if (!clientProducts.has(product)) {
                            passesProductInclusion = false;
                            break;
                        }
                    }
                }
            }

            let passesProductLevelOneInclusion = true;
            if (productLevelOnes.length > 0) {
                if (currentProductLevelOneLogic === "or") {
                    passesProductLevelOneInclusion = false;
                    for (const level of productLevelOnes) {
                        if (clientProductLevelOnes.has(level)) {
                            passesProductLevelOneInclusion = true;
                            break;
                        }
                    }
                } else {
                    // 'and' logic
                    passesProductLevelOneInclusion = true;
                    for (const level of productLevelOnes) {
                        if (!clientProductLevelOnes.has(level)) {
                            passesProductLevelOneInclusion = false;
                            break;
                        }
                    }
                }
            }

            const passesInclusionFilter =
                passesBrandInclusion &&
                passesProductInclusion &&
                passesProductLevelOneInclusion;

            // Apply exclusion filters (client must NOT have any selected excluded brand OR product OR product level one)
            let passesExclusionFilter = true;

            // Check excluded brands
            if (excludedBrands.length > 0) {
                for (const brand of excludedBrands) {
                    if (clientBrands.has(brand)) {
                        passesExclusionFilter = false;
                        break;
                    }
                }
            }

            // If still passing, check excluded products
            if (passesExclusionFilter && excludedProducts.length > 0) {
                for (const product of excludedProducts) {
                    if (clientProducts.has(product)) {
                        passesExclusionFilter = false;
                        break;
                    }
                }
            }

            // If still passing, check excluded product level ones
            if (passesExclusionFilter && excludedProductLevelOnes.length > 0) {
                for (const level of excludedProductLevelOnes) {
                    if (clientProductLevelOnes.has(level)) {
                        passesExclusionFilter = false;
                        break;
                    }
                }
            }

            if (passesInclusionFilter && passesExclusionFilter) {
                // Process transactions in smaller chunks to avoid stack overflows
                for (let j = 0; j < clientTransactions.length; j++) {
                    const transaction = clientTransactions[j];
                    // Create a clean new object without any references to the original
                    filteredClientsData.push({
                        year: transaction.year,
                        month: transaction.month,
                        client_name: transaction.client_name,
                        brand: transaction.brand,
                        product_name: transaction.product_name,
                        product_level_1: transaction.product_level_1,
                        total_volume: transaction.total_volume,
                        total_revenue: transaction.total_revenue,
                        warehouse_name: transaction.warehouse_name || "",
                        city: transaction.city,
                    });
                }
            }
        }

        setFilteredData(filteredClientsData);
    };

    useEffect(() => {
        // Fetch and process data on initial mount
        fetchAndProcessData();
    }, []);

    useEffect(() => {
        // Re-apply filters when selected brands or products change
        applyFilters(
            clientData,
            selectedBrands,
            selectedProducts,
            selectedExcludedBrands,
            selectedExcludedProducts,
            brandLogic,
            productLogic,
            productLevelOneLogic,
            selectedProductLevelOnes,
            selectedExcludedProductLevelOnes
        );
    }, [
        selectedBrands,
        selectedProducts,
        clientData,
        selectedExcludedBrands,
        selectedExcludedProducts,
        brandLogic,
        productLogic,
        productLevelOneLogic,
        selectedProductLevelOnes,
        selectedExcludedProductLevelOnes,
    ]); // Depend on clientData and all filter selections

    const handleBrandChange = (event: SelectChangeEvent<string[]>) => {
        const { value } = event.target;
        setSelectedBrands(typeof value === "string" ? value.split(",") : value);
    };

    const handleProductChange = (event: SelectChangeEvent<string[]>) => {
        const { value } = event.target;
        setSelectedProducts(
            typeof value === "string" ? value.split(",") : value
        );
    };

    const handleProductLevelOneChange = (
        event: SelectChangeEvent<string[]>
    ) => {
        const { value } = event.target;
        setSelectedProductLevelOnes(
            typeof value === "string" ? value.split(",") : value
        );
    };

    const handleBrandLogicChange = (event: SelectChangeEvent<"and" | "or">) => {
        setBrandLogic(event.target.value as "and" | "or");
    };

    const handleProductLogicChange = (
        event: SelectChangeEvent<"and" | "or">
    ) => {
        setProductLogic(event.target.value as "and" | "or");
    };

    const handleProductLevelOneLogicChange = (
        event: SelectChangeEvent<"and" | "or">
    ) => {
        setProductLevelOneLogic(event.target.value as "and" | "or");
    };

    const handleExcludedBrandChange = (event: SelectChangeEvent<string[]>) => {
        const { value } = event.target;
        setSelectedExcludedBrands(
            typeof value === "string" ? value.split(",") : value
        );
    };

    const handleExcludedProductChange = (
        event: SelectChangeEvent<string[]>
    ) => {
        const { value } = event.target;
        setSelectedExcludedProducts(
            typeof value === "string" ? value.split(",") : value
        );
    };

    const handleRemoveExcludedProduct = (valueToRemove: string) => {
        setSelectedExcludedProducts(
            selectedExcludedProducts.filter((value) => value !== valueToRemove)
        );
    };

    const handleRemoveBrand = (valueToRemove: string) => {
        setSelectedBrands(
            selectedBrands.filter((value) => value !== valueToRemove)
        );
    };

    const handleRemoveProduct = (valueToRemove: string) => {
        setSelectedProducts(
            selectedProducts.filter((value) => value !== valueToRemove)
        );
    };

    const handleRemoveExcludedBrand = (valueToRemove: string) => {
        setSelectedExcludedBrands(
            selectedExcludedBrands.filter((value) => value !== valueToRemove)
        );
    };

    const handleRemoveProductLevelOne = (valueToRemove: string) => {
        setSelectedProductLevelOnes(
            selectedProductLevelOnes.filter((value) => value !== valueToRemove)
        );
    };

    const handleExcludedProductLevelOneChange = (
        event: SelectChangeEvent<string[]>
    ) => {
        const { value } = event.target;
        setSelectedExcludedProductLevelOnes(
            typeof value === "string" ? value.split(",") : value
        );
    };

    const handleRemoveExcludedProductLevelOne = (valueToRemove: string) => {
        setSelectedExcludedProductLevelOnes(
            selectedExcludedProductLevelOnes.filter(
                (value) => value !== valueToRemove
            )
        );
    };

    const filteredBrands = availableBrands.filter((brand) =>
        brand.toLowerCase().includes(brandSearchTerm.toLowerCase())
    );

    const filteredProducts = availableProducts.filter((product) =>
        product.toLowerCase().includes(productSearchTerm.toLowerCase())
    );

    const filteredExcludedBrands = availableBrands.filter((brand) =>
        brand.toLowerCase().includes(excludedBrandSearchTerm.toLowerCase())
    );

    const filteredExcludedProducts = availableProducts.filter((product) =>
        product.toLowerCase().includes(excludedProductSearchTerm.toLowerCase())
    );

    const filteredProductLevelOnes = availableProductLevelOnes.filter((level) =>
        level.toLowerCase().includes(productLevelOneSearchTerm.toLowerCase())
    );

    const filteredExcludedProductLevelOnes = availableProductLevelOnes.filter(
        (level) =>
            level
                .toLowerCase()
                .includes(excludedProductLevelOneSearchTerm.toLowerCase())
    );

    const columns: GridColDef[] = [
        {
            field: "warehouse_name",
            headerName: "Entrepôt",
            width: 200,
        },
        {
            field: "city",
            headerName: "Ville",
            flex: 1,
        },
        {
            field: "client_name",
            headerName: "Client",
            width: 300,
        },
        {
            field: "brand",
            headerName: "Marque",
            flex: 1,
        },
        {
            field: "product_level_1",
            headerName: "Catégorie de produit",
            flex: 1,
        },

        {
            field: "product_name",
            headerName: "Produit",
            flex: 2,
        },
        {
            field: "total_volume",
            headerName: "Volume (L)",
            type: "number",
            flex: 1,
        },
        {
            field: "total_revenue",
            headerName: "CA HTHD",
            type: "number",
            flex: 1,
        },
    ];

    return (
        <Box mt={4} style={{ height: 900, width: "100%" }}>
            {/* Filter Controls */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "0.5fr 0.25fr 0.25fr", // Column 1: Chips, Column 2: Select, Column 3: Logic
                    gap: 2, // Gap between grid items
                    mb: 2, // Margin bottom for the whole filter section
                    alignItems: "center", // Vertically align items in the grid
                }}
            >
                {/* Row 1: Bought Brands */}
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        alignItems: "center",

                        p: 1,
                        minHeight: 40,
                        borderRadius: 1,
                        backgroundColor: "#f5f5f5",
                    }}
                >
                    {selectedProductLevelOnes.map((value, index) => (
                        <React.Fragment key={value}>
                            <Chip
                                label={value}
                                onDelete={() =>
                                    handleRemoveProductLevelOne(value)
                                }
                                sx={{ backgroundColor: "white" }}
                            />
                            {index < selectedProductLevelOnes.length - 1 &&
                                selectedProductLevelOnes.length > 1 && (
                                    <Typography
                                        variant="body2"
                                        sx={{ mx: 0.5 }}
                                    >
                                        {productLevelOneLogic === "or"
                                            ? "ou"
                                            : "et"}
                                    </Typography>
                                )}
                        </React.Fragment>
                    ))}
                </Box>
                <FormControl
                    sx={{ minWidth: 200, maxWidth: 300, fontSize: "small" }}
                    size="small"
                >
                    <InputLabel
                        id="product-level-one-select-label"
                        sx={{ fontSize: "small" }}
                    >
                        Catégories de produit Achetées
                    </InputLabel>
                    <Select<string[]>
                        labelId="product-level-one-select-label"
                        multiple
                        value={selectedProductLevelOnes}
                        onChange={handleProductLevelOneChange}
                        input={<OutlinedInput />}
                    >
                        <Box sx={{ padding: "8px" }}>
                            <TextField
                                autoFocus
                                size="small"
                                placeholder="Rechercher catégories produit..."
                                value={productLevelOneSearchTerm}
                                onChange={(e) =>
                                    setProductLevelOneSearchTerm(e.target.value)
                                }
                                onKeyDown={(e) => e.stopPropagation()}
                            />
                        </Box>
                        {filteredProductLevelOnes.map((level) => (
                            <MenuItem
                                key={level}
                                value={level}
                                sx={{ fontSize: "small" }}
                            >
                                {level}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl
                    sx={{ minWidth: 100, fontSize: "small" }}
                    size="small"
                >
                    <InputLabel
                        id="brand-logic-select-label"
                        sx={{ fontSize: "small" }}
                    >
                        Logique Catégories Achetées
                    </InputLabel>
                    <Select
                        labelId="brand-logic-select-label"
                        value={productLevelOneLogic}
                        label="Logique Marques Achetées"
                        onChange={handleProductLevelOneLogicChange}
                    >
                        <MenuItem value="or">ou</MenuItem>
                        <MenuItem value="and">et</MenuItem>
                    </Select>
                </FormControl>
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        alignItems: "center",
                        p: 1,
                        minHeight: 40,
                        borderRadius: 1,
                        backgroundColor: "#f5f5f5",
                    }}
                >
                    {selectedBrands.map((value, index) => (
                        <React.Fragment key={value}>
                            <Chip
                                label={value}
                                onDelete={() => handleRemoveBrand(value)}
                                sx={{ backgroundColor: "white" }}
                            />
                            {index < selectedBrands.length - 1 &&
                                selectedBrands.length > 1 && (
                                    <Typography
                                        variant="body2"
                                        sx={{ mx: 0.5 }}
                                    >
                                        {brandLogic === "or" ? "ou" : "et"}
                                    </Typography>
                                )}
                        </React.Fragment>
                    ))}
                </Box>
                <FormControl
                    sx={{ minWidth: 200, maxWidth: 300, fontSize: "small" }}
                    size="small"
                >
                    <InputLabel
                        id="brand-select-label"
                        sx={{ fontSize: "small" }}
                    >
                        Marques Achetées
                    </InputLabel>
                    <Select<string[]>
                        labelId="brand-select-label"
                        multiple
                        value={selectedBrands}
                        onChange={handleBrandChange}
                        input={<OutlinedInput />}
                    >
                        <Box sx={{ padding: "8px" }}>
                            <TextField
                                autoFocus
                                size="small"
                                placeholder="Rechercher marques achetées..."
                                value={brandSearchTerm}
                                onChange={(e) =>
                                    setBrandSearchTerm(e.target.value)
                                }
                                onKeyDown={(e) => e.stopPropagation()}
                            />
                        </Box>
                        {filteredBrands.map((brand) => (
                            <MenuItem
                                key={brand}
                                value={brand}
                                sx={{ fontSize: "small" }}
                            >
                                {brand}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl
                    sx={{ minWidth: 100, fontSize: "small" }}
                    size="small"
                >
                    <InputLabel
                        id="brand-logic-select-label"
                        sx={{ fontSize: "small" }}
                    >
                        Logique Marques Achetées
                    </InputLabel>
                    <Select
                        labelId="brand-logic-select-label"
                        value={brandLogic}
                        label="Logique Marques Achetées"
                        onChange={handleBrandLogicChange}
                    >
                        <MenuItem value="or">ou</MenuItem>
                        <MenuItem value="and">et</MenuItem>
                    </Select>
                </FormControl>

                {/* Row 2: Bought Products */}
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        alignItems: "center",
                        p: 1,
                        minHeight: 40,
                        borderRadius: 1,
                        backgroundColor: "#f5f5f5",
                    }}
                >
                    {selectedProducts.map((value, index) => (
                        <React.Fragment key={value}>
                            <Chip
                                label={value}
                                onDelete={() => handleRemoveProduct(value)}
                                sx={{ backgroundColor: "white" }}
                            />
                            {index < selectedProducts.length - 1 &&
                                selectedProducts.length > 1 && (
                                    <Typography
                                        variant="body2"
                                        sx={{ mx: 0.5 }}
                                    >
                                        {productLogic === "or" ? "ou" : "et"}
                                    </Typography>
                                )}
                        </React.Fragment>
                    ))}
                </Box>
                <FormControl
                    sx={{ minWidth: 200, maxWidth: 300, fontSize: "small" }}
                    size="small"
                >
                    <InputLabel
                        id="product-select-label"
                        sx={{ fontSize: "small" }}
                    >
                        Produits Achetés
                    </InputLabel>
                    <Select<string[]>
                        labelId="product-select-label"
                        multiple
                        value={selectedProducts}
                        onChange={handleProductChange}
                        input={<OutlinedInput />}
                    >
                        <Box sx={{ padding: "8px" }}>
                            <TextField
                                autoFocus
                                size="small"
                                placeholder="Rechercher produits achetés..."
                                value={productSearchTerm}
                                onChange={(e) =>
                                    setProductSearchTerm(e.target.value)
                                }
                                onKeyDown={(e) => e.stopPropagation()}
                            />
                        </Box>
                        {filteredProducts.map((product) => (
                            <MenuItem
                                key={product}
                                value={product}
                                sx={{ fontSize: "small" }}
                            >
                                {product}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl
                    sx={{ minWidth: 100, fontSize: "small" }}
                    size="small"
                >
                    <InputLabel
                        id="product-logic-select-label"
                        sx={{ fontSize: "small" }}
                    >
                        Logique Produits Achetés
                    </InputLabel>
                    <Select
                        labelId="product-logic-select-label"
                        value={productLogic}
                        label="Logique Produits Achetés"
                        onChange={handleProductLogicChange}
                    >
                        <MenuItem value="or">ou</MenuItem>
                        <MenuItem value="and">et</MenuItem>
                    </Select>
                </FormControl>

                {/* Row 3: Bought Product Level Ones */}

                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        alignItems: "center",
                        p: 1,
                        minHeight: 40,
                        borderRadius: 1,
                        backgroundColor: "#f5f5f5",
                    }}
                >
                    {/* Column 1: Chips */}
                    {selectedExcludedProductLevelOnes.map((value, index) => (
                        <React.Fragment key={value}>
                            <Chip
                                label={value}
                                onDelete={() =>
                                    handleRemoveExcludedProductLevelOne(value)
                                }
                                sx={{ backgroundColor: "white" }}
                            />
                            {index <
                                selectedExcludedProductLevelOnes.length - 1 &&
                                selectedExcludedProductLevelOnes.length > 1 && (
                                    <Typography
                                        variant="body2"
                                        sx={{ mx: 0.5 }}
                                    >
                                        ou{" "}
                                        {/* Exclusion logic is typically OR */}
                                    </Typography>
                                )}
                        </React.Fragment>
                    ))}
                </Box>
                <FormControl
                    sx={{ minWidth: 200, maxWidth: 300, fontSize: "small" }}
                    size="small"
                >
                    {/* Column 2: Select */}
                    <InputLabel
                        id="excluded-product-level-one-select-label"
                        sx={{ fontSize: "small" }}
                    >
                        Catégories de produit Non Achetées
                    </InputLabel>
                    <Select<string[]>
                        labelId="excluded-product-level-one-select-label"
                        multiple
                        value={selectedExcludedProductLevelOnes}
                        onChange={handleExcludedProductLevelOneChange}
                        input={<OutlinedInput />}
                    >
                        <Box sx={{ padding: "8px" }}>
                            <TextField
                                autoFocus
                                size="small"
                                placeholder="Rechercher catégories produit non achetées..."
                                value={excludedProductLevelOneSearchTerm}
                                onChange={(e) =>
                                    setExcludedProductLevelOneSearchTerm(
                                        e.target.value
                                    )
                                }
                                onKeyDown={(e) => e.stopPropagation()}
                            />
                        </Box>
                        {filteredExcludedProductLevelOnes.map((level) => (
                            <MenuItem
                                key={level}
                                value={level}
                                sx={{ fontSize: "small" }}
                            >
                                {level}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Box>{/* Column 3: Empty */}</Box>

                {/* Row 4: Brands Not Purchased */}
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        alignItems: "center",
                        p: 1,
                        minHeight: 40,
                        borderRadius: 1,
                        backgroundColor: "#f5f5f5",
                    }}
                >
                    {selectedExcludedBrands.map((value, index) => (
                        <React.Fragment key={value}>
                            <Chip
                                label={value}
                                onDelete={() =>
                                    handleRemoveExcludedBrand(value)
                                }
                                sx={{ backgroundColor: "white" }}
                            />
                            {index < selectedExcludedBrands.length - 1 &&
                                selectedExcludedBrands.length > 1 && (
                                    <Typography
                                        variant="body2"
                                        sx={{ mx: 0.5 }}
                                    >
                                        ou
                                    </Typography>
                                )}
                        </React.Fragment>
                    ))}
                </Box>
                <FormControl
                    sx={{ minWidth: 200, maxWidth: 300, fontSize: "small" }}
                    size="small"
                >
                    <InputLabel
                        id="excluded-brand-select-label"
                        sx={{ fontSize: "small" }}
                    >
                        Marques Non Achetées
                    </InputLabel>
                    <Select<string[]>
                        labelId="excluded-brand-select-label"
                        multiple
                        value={selectedExcludedBrands}
                        onChange={handleExcludedBrandChange}
                        input={<OutlinedInput />}
                    >
                        <Box sx={{ padding: "8px" }}>
                            <TextField
                                autoFocus
                                size="small"
                                placeholder="Rechercher marques non achetées..."
                                value={excludedBrandSearchTerm}
                                onChange={(e) =>
                                    setExcludedBrandSearchTerm(e.target.value)
                                }
                                onKeyDown={(e) => e.stopPropagation()}
                            />
                        </Box>
                        {filteredExcludedBrands.map((brand) => (
                            <MenuItem
                                key={brand}
                                value={brand}
                                sx={{ fontSize: "small" }}
                            >
                                {brand}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Box>{/* Column 3: Empty */}</Box>

                {/* Row 5: Products Not Purchased */}
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        alignItems: "center",

                        p: 1,
                        minHeight: 40,
                        borderRadius: 1,
                        backgroundColor: "#f5f5f5",
                    }}
                >
                    {selectedExcludedProducts.map((value, index) => (
                        <React.Fragment key={value}>
                            <Chip
                                label={value}
                                onDelete={() =>
                                    handleRemoveExcludedProduct(value)
                                }
                                sx={{ backgroundColor: "white" }}
                            />
                            {index < selectedExcludedProducts.length - 1 &&
                                selectedExcludedProducts.length > 1 && (
                                    <Typography
                                        variant="body2"
                                        sx={{ mx: 0.5 }}
                                    >
                                        {productLogic === "or" ? "ou" : "et"}
                                    </Typography>
                                )}
                        </React.Fragment>
                    ))}
                </Box>
                <FormControl
                    sx={{ minWidth: 200, maxWidth: 300, fontSize: "small" }}
                    size="small"
                >
                    <InputLabel
                        id="excluded-product-select-label"
                        sx={{ fontSize: "small" }}
                    >
                        Produits Non Achetés
                    </InputLabel>
                    <Select<string[]>
                        labelId="excluded-product-select-label"
                        multiple
                        value={selectedExcludedProducts}
                        onChange={handleExcludedProductChange}
                        input={<OutlinedInput />}
                    >
                        <Box sx={{ padding: "8px" }}>
                            <TextField
                                autoFocus
                                size="small"
                                placeholder="Rechercher produits non achetés..."
                                value={excludedProductSearchTerm}
                                onChange={(e) =>
                                    setExcludedProductSearchTerm(e.target.value)
                                }
                                onKeyDown={(e) => e.stopPropagation()}
                            />
                        </Box>
                        {filteredExcludedProducts.map((product) => (
                            <MenuItem
                                key={product}
                                value={product}
                                sx={{ fontSize: "small" }}
                            >
                                {product}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Box>{/* Column 3: Empty */}</Box>
            </Box>
            {loading ? (
                <CircularProgress />
            ) : (
                <DataGridPremium
                    rows={(filteredData || []).map((row, index) => ({
                        id: index,
                        ...row,
                    }))}
                    columns={columns}
                    showToolbar
                    apiRef={apiRef}
                    initialState={{
                        columns: {
                            columnVisibilityModel: {
                                warehouse_name: false,
                                city: false,
                                client_name: false,
                                brand: false,
                                product_level_1: false,
                            },
                        },
                        rowGrouping: {
                            model: [
                                "warehouse_name",
                                "city",
                                "client_name",
                                "product_level_1",
                                "brand",
                            ],
                        },
                    }}
                    rowGroupingColumnMode="multiple"
                    disableRowSelectionOnClick
                    localeText={
                        frFR.components.MuiDataGrid.defaultProps.localeText
                    }
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                        toolbar: {
                            csvOptions: {
                                fileName: `Transactions_${dayjs().format(
                                    "YYYY-MM-DD"
                                )}`,
                            },
                            excelOptions: {
                                fileName: `Transactions_${dayjs().format(
                                    "YYYY-MM-DD"
                                )}`,
                            },
                        },
                    }}
                    sx={{
                        "& .MuiButton-root": {
                            color: "#000000",
                            "&:hover": {
                                backgroundColor: "#222",
                            },
                        },
                        "& .MuiSelect-root, & .MuiInputBase-root, & .MuiOutlinedInput-root":
                            {
                                color: "#fff",
                                "& .MuiSvgIcon-root": {
                                    color: "#fff",
                                },
                            },
                        "& .MuiInputBase-input": {
                            color: "#fff",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#000",
                        },
                        "& .MuiDataGrid-toolbarContainer": {
                            gap: 1,
                        },
                    }}
                    defaultGroupingExpansionDepth={2}
                />
            )}
        </Box>
    );
};

export default ClientBrandProductTable;
